import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { API_BASE_URL } from '@/lib/api';

const SUBMIT_URL = `${API_BASE_URL}/contact`;

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string | null)?.trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setErrorMsg(null);

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        responseText;

      if (!response.ok || parsed?.code !== 'OK' || serverMsg.toLowerCase().includes('spam')) {
        setStatus('error');
        setErrorMsg(serverMsg || 'Erreur lors de l\'envoi. Réessaie plus tard.');
        return;
      }

      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Erreur de connexion.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar onCreateClick={() => { }} createLabel="Publier" />

      <section className="relative w-full overflow-hidden bg-primary-500">
        <div className="px-4 md:px-8 py-[1rem] md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm font-medium">
              <i className="ri-mail-line"></i>
              Contact
            </span>
            <h1 className="mt-5 font-heading text-[1.5rem] md:text-5xl font-semibold text-white leading-tight">
              Une question ? On est là.
            </h1>
            <p className="mt-4 text-[1rem] md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
              Que ce soit pour signaler un problème, proposer une idée ou simplement dire bonjour, n'hésite pas à nous écrire.
            </p>
          </div>
        </div>
      </section>

      <main className="flex-1 w-full px-4 md:px-8 py-[1rem] md:py-14">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          {/* Infos */}
          <div className="md:col-span-1 grid grid-cols-3 gap-x-[.3rem] md:grid-cols-1 md:gap-x-0 md:gap-y-6">
            <div className="bg-white border border-background-200 rounded-lg py-[.5rem] px-2 sm:p-5">
              <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 mb-2 sm:mb-3">
                <i className="ri-mail-line text-sm sm:text-lg"></i>
              </div>

              <h3 className="font-bold text-foreground-950 text-[.68rem] sm:text-sm">
                Nous contacter
              </h3>

              <p className="mt-1 text-[.6rem] font-semibold sm:text-sm text-foreground-500">
                Écris-nous via le formulaire.
              </p>
            </div>

            <div className="bg-white border border-background-200 rounded-lg py-[.5rem] px-2 sm:p-5">
              <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 mb-2 sm:mb-3">
                <i className="ri-map-pin-2-line text-sm sm:text-lg"></i>
              </div>

              <h3 className="font-bold text-foreground-950 text-[.68rem] sm:text-sm">
                Localisation
              </h3>

              <p className="mt-1 text-[.6rem] font-semibold sm:text-sm text-foreground-500">
                Grand Dole, Jura (39)
              </p>
            </div>

            <div className="bg-white border border-background-200 rounded-lg py-[.5rem] px-2 sm:p-5">
              <div className="w-6 h-6 sm:w-10 sm:h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 mb-2 sm:mb-3">
                <i className="ri-time-line text-sm sm:text-lg"></i>
              </div>

              <h3 className="font-bold text-foreground-950 text-[.68rem] sm:text-sm">
                Délai de réponse
              </h3>

              <p className="mt-1 text-[.6rem] font-semibold sm:text-sm text-foreground-500">
                En général sous 48 heures ouvrées.
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="bg-white border border-background-200 rounded-lg p-6 md:p-8">
              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary-50 flex items-center justify-center text-primary-600">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </div>
                  <h2 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
                    Message envoyé
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500 max-w-sm mx-auto">
                    Merci ! On a bien reçu ton message et on te répondra au plus vite.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-[.8rem] sm:gap-5"
                >
                  <h2 className="font-heading text-xl font-semibold text-foreground-950">
                    Écris-nous
                  </h2>

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Nom
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Ton nom"
                      className="w-full px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="toi@exemple.fr"
                      className="w-full px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Sujet
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      placeholder="Ex : Problème de connexion, suggestion..."
                      className="w-full px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      placeholder="Dis-nous tout..."
                      className="w-full px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                    />
                    <p className="mt-1 text-xs text-foreground-400 text-right">500 caractères max</p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="company-alt-hp"
                  />

                  {errorMsg && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                      <i className="ri-error-warning-line mt-0.5"></i>
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' && <i className="ri-loader-4-line animate-spin"></i>}
                    Envoyer le message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}